import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useKeyboardEvensts from "./useKeyboardEvents.js";
import useFullscreen from "./useFullscreen.js";
import usePoppedOut from "./usePoppedOut.js";
export default function useControls(opts) {
  /**
   * @type {{
   *  delay: number;
   *  videoRef: import('react').Ref<HTMLVideoElement>
   * }}
   */
  const {
    createPlayer,
    delay = 5000,
    destroyTimerRef,
    isLoadedRef,
    playerRef,
    videoRef,
    containerRef,
    loading,
    setLoading,
    playing,
    setPlaying
  } = opts;
  const timerRef = useRef();
  const controlsRef = useRef();
  const [controlsVisible, setControlsVisible] = useState();
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const afterMouseMove = useCallback(() => {
    setControlsVisible(false);
  }, []);
  const onMouseMove = useCallback(evt => {
    setControlsVisible(true);
    clearTimeout(timerRef.current);
    if (evt.target !== controlsRef.current && !controlsRef.current.contains(evt.target)) {
      timerRef.current = setTimeout(afterMouseMove, delay);
    }
  }, [afterMouseMove, delay]);
  useEffect(() => {
    videoRef.current.addEventListener('loadedmetadata', evt => {
      setMetadataLoaded(true);
    });
  }, []);
  const fullscreenSupported = useMemo(() => {
    return typeof containerRef.current?.requestFullscreen === 'function';
  }, [containerRef.current]);
  const pictureInPictureSupported = useMemo(() => {
    return metadataLoaded && typeof videoRef.current?.requestPictureInPicture === 'function';
  }, [metadataLoaded, videoRef.current]);
  const onFullscreen = useCallback(() => {
    if (fullscreenSupported) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current?.requestFullscreen();
      }
    }
  }, [containerRef.current, fullscreenSupported]);
  const onPictureInPicture = useCallback(() => {
    if (pictureInPictureSupported && metadataLoaded) {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture();
      } else {
        videoRef.current?.requestPictureInPicture();
      }
    }
  }, [videoRef.current, pictureInPictureSupported, metadataLoaded]);
  const onPlayPause = useCallback(async () => {
    if (!playing) {
      setLoading(true);
      clearTimeout(destroyTimerRef.current);
      if (!playerRef.current) {
        playerRef.current = createPlayer();
      }
      if (!isLoadedRef.current) {
        playerRef.current.load();
        isLoadedRef.current = true;
      }
      await playerRef.current?.play();
      setPlaying(true);
      setLoading(false);
    } else {
      playerRef.current?.pause();
      setPlaying(false);
      destroyTimerRef.current = setTimeout(() => {
        playerRef.current?.unload();
        isLoadedRef.current = false;
        playerRef.current?.destroy();
        playerRef.current = null;
      }, 5000);
    }
  }, [playing, createPlayer]);
  useKeyboardEvensts({
    playing,
    loading,
    onPlayPause,
    onFullscreen,
    onPictureInPicture
  });
  const isFullscreen = useFullscreen();
  const isPoppedOut = usePoppedOut({
    ref: videoRef
  });
  return {
    visible: controlsVisible,
    onMouseMove,
    ref: controlsRef,
    onFullscreen,
    onPictureInPicture,
    onPlayPause,
    fullscreenSupported,
    pictureInPictureSupported,
    loading,
    playing,
    isFullscreen,
    isPoppedOut
  };
}